import { useCallback, useLayoutEffect, useMemo, useRef, useState, memo } from 'react'

const getItemKey = (item, index, itemKey) => {
  if (typeof itemKey === 'function') return itemKey(item, index)
  if (typeof itemKey === 'string') return item?.[itemKey]
  return item?.id ?? index
}

const resolveScrollElement = (scrollElement, container) => {
  if (scrollElement && typeof scrollElement === 'object' && 'current' in scrollElement) {
    return scrollElement.current ?? container
  }
  return scrollElement || container
}

const getContainerTop = (container, scrollElement) => {
  if (!container || !scrollElement) return 0
  const containerRect = container.getBoundingClientRect()
  const scrollRect = scrollElement.getBoundingClientRect()
  return containerRect.top - scrollRect.top + scrollElement.scrollTop
}

const findStartIndex = (offsets, scrollTop) => {
  let low = 0
  let high = offsets.length - 1
  let mid = 0
  while (low <= high) {
    mid = Math.floor((low + high) / 2)
    if (offsets[mid] <= scrollTop) {
      low = mid + 1
    } else {
      high = mid - 1
    }
  }
  return Math.max(0, low - 1)
}

const VirtualListRow = memo(
  ({ item, index, itemRenderer: ItemRenderer, itemData, offset, useAbsolute, itemGap, onSize }) => {
    const rowRef = useRef(null)
    const setRowRef = useCallback((node) => {
      rowRef.current = node
    }, [])
    useLayoutEffect(() => {
      if (!onSize || !rowRef.current) return undefined
      const node = rowRef.current
      const updateSize = () => {
        onSize(index, node.getBoundingClientRect().height)
      }
      const observer = new ResizeObserver(updateSize)
      observer.observe(node)
      updateSize()
      return () => observer.disconnect()
    }, [index, onSize])
    const rowStyle = useMemo(() => {
      if (!useAbsolute) {
        return itemGap ? { paddingBottom: itemGap } : undefined
      }
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        transform: `translateY(${offset}px)`,
        paddingBottom: itemGap,
      }
    }, [itemGap, offset, useAbsolute])
    return (
      <div ref={setRowRef} style={rowStyle}>
        <ItemRenderer item={item} index={index} {...itemData} />
      </div>
    )
  },
  (prev, next) => {
    return (
      prev.item === next.item &&
      prev.index === next.index &&
      prev.offset === next.offset &&
      prev.useAbsolute === next.useAbsolute &&
      prev.itemGap === next.itemGap &&
      prev.itemData === next.itemData // Rely on itemData memoization in parent
    )
  },
)

export function VirtualList({
  items,
  itemKey,
  itemRenderer,
  itemData,
  estimatedItemHeight = 48,
  overscan = 6,
  minItems = 50, // Lowered default
  itemGap = 0,
  scrollElement,
  className,
  contentClassName,
}) {
  const containerRef = useRef(null)
  const rafRef = useRef(0)
  const [sizeMap, setSizeMap] = useState(() => new Map())
  const [scrollTarget, setScrollTarget] = useState(null)
  const [metrics, setMetrics] = useState({
    scrollTop: 0,
    viewportHeight: 0,
    containerTop: 0,
  })
  const isVirtual = items.length >= minItems
  const setItemSize = useCallback((index, size) => {
    setSizeMap((prev) => {
      if (prev.get(index) === size) return prev
      const next = new Map(prev)
      next.set(index, size)
      return next
    })
  }, [])
  const getItemSize = useCallback(
    (index) => sizeMap.get(index) ?? estimatedItemHeight,
    [estimatedItemHeight, sizeMap],
  )
  const { offsets, totalSize } = useMemo(() => {
    const nextOffsets = new Array(items.length)
    let offset = 0
    for (let i = 0; i < items.length; i += 1) {
      nextOffsets[i] = offset
      offset += getItemSize(i)
    }
    return { offsets: nextOffsets, totalSize: offset }
  }, [getItemSize, items.length])
  useLayoutEffect(() => {
    const nextTarget = resolveScrollElement(scrollElement, containerRef.current)
    setScrollTarget((prev) => (prev === nextTarget ? prev : nextTarget))
  }, [scrollElement])
  const updateMetrics = useCallback(() => {
    if (!scrollTarget || !containerRef.current) return
    const containerTop = getContainerTop(containerRef.current, scrollTarget)
    const viewportHeight = scrollTarget.clientHeight
    setMetrics({
      scrollTop: scrollTarget.scrollTop,
      viewportHeight,
      containerTop,
    })
  }, [scrollTarget])
  const handleScroll = useCallback(() => {
    if (rafRef.current) return
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = 0
      updateMetrics()
    })
  }, [updateMetrics])
  useLayoutEffect(() => {
    updateMetrics()
  }, [updateMetrics, items.length])
  useLayoutEffect(() => {
    if (!scrollTarget) return undefined
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true })
    const observer = new ResizeObserver(updateMetrics)
    observer.observe(scrollTarget)
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }
    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll)
      observer.disconnect()
    }
  }, [handleScroll, scrollTarget, updateMetrics])
  if (!isVirtual) {
    return (
      <div ref={containerRef} className={className}>
        <div className={contentClassName}>
          {items.map((item, index) => (
            <VirtualListRow
              key={getItemKey(item, index, itemKey)}
              item={item}
              index={index}
              itemRenderer={itemRenderer}
              itemData={itemData}
              itemGap={itemGap}
            />
          ))}
        </div>
      </div>
    )
  }
  const relativeScrollTop = metrics.scrollTop - metrics.containerTop
  const overscanPx = overscan * estimatedItemHeight
  const startIndex = findStartIndex(offsets, Math.max(0, relativeScrollTop - overscanPx))
  let endIndex = startIndex
  const maxOffset = relativeScrollTop + metrics.viewportHeight + overscanPx
  while (endIndex < offsets.length && offsets[endIndex] < maxOffset) {
    endIndex += 1
  }
  const visibleItems = items.slice(startIndex, endIndex)
  return (
    <div ref={containerRef} className={className}>
      <div className={contentClassName} style={{ height: totalSize, position: 'relative' }}>
        {visibleItems.map((item, offsetIndex) => {
          const index = startIndex + offsetIndex
          return (
            <VirtualListRow
              key={getItemKey(item, index, itemKey)}
              item={item}
              index={index}
              itemRenderer={itemRenderer}
              itemData={itemData}
              offset={offsets[index]}
              useAbsolute
              itemGap={itemGap}
              onSize={setItemSize}
            />
          )
        })}
      </div>
    </div>
  )
}
